import type {
  ColumnView,
  OpensipsReloadResult,
  PreviewAction
} from '../../../services/opensips.types';
import Icon from '../../../components/forms/icon.component';

const ACTION_LABEL: Record<PreviewAction, { text: string; badge: string }> = {
  create: { text: 'INSERT', badge: 'badge-light-success' },
  update: { text: 'UPDATE', badge: 'badge-light-warning' },
  delete: { text: 'DELETE', badge: 'badge-light-danger' },
  capacity: { text: 'UPDATE capacidad', badge: 'badge-light-warning' }
};

const show = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? <span className="text-muted">NULL</span> : String(v);

/**
 * Muestra la fila que devolvió `?dryRun=true` tal cual quedaría en la tabla de OpenSIPS
 * (llaves = columnas reales). Si se pasa `before` (fila actual) resalta las columnas que cambian.
 */
export function OpensipsPreview({
  table,
  action,
  record,
  before
}: {
  table: string;
  action: PreviewAction;
  record: ColumnView;
  before?: ColumnView | null;
}) {
  const cols = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(record)]));
  const a = ACTION_LABEL[action] ?? ACTION_LABEL.update;
  const changed = cols.filter((c) => before && String(before[c] ?? '') !== String(record[c] ?? ''));

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-3">
        <span className={`badge ${a.badge} fs-8 fw-bold`}>{a.text}</span>
        <code className="fs-7">{table}</code>
        {before && (
          <span className="text-muted fs-8">
            {changed.length === 0
              ? 'sin cambios respecto a la fila actual'
              : `${changed.length} columna(s) cambian`}
          </span>
        )}
      </div>
      <div className="table-responsive border rounded-3">
        <table className="table table-sm table-row-dashed fs-7 mb-0 font-monospace">
          <thead className="bg-light">
            <tr>
              <th className="ps-3">columna</th>
              {before && <th>actual</th>}
              <th>{before ? 'nuevo' : 'valor'}</th>
            </tr>
          </thead>
          <tbody>
            {cols.map((c) => {
              const isChanged = changed.includes(c);
              return (
                <tr key={c} className={isChanged ? 'bg-light-warning' : ''}>
                  <td className="ps-3 text-gray-600">{c}</td>
                  {before && (
                    <td className={isChanged ? 'text-decoration-line-through text-muted' : ''}>
                      {show(before[c])}
                    </td>
                  )}
                  <td className={isChanged ? 'fw-bold' : ''}>{show(record[c])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Resultado del reload MI que el backend dispara tras escribir. */
export function ReloadBanner({
  reload,
  what
}: {
  reload?: OpensipsReloadResult | null;
  what: string;
}) {
  if (!reload) return null;
  const cls = !reload.triggered ? 'alert-secondary' : reload.ok ? 'alert-success' : 'alert-danger';
  const icon = !reload.triggered
    ? 'ki-information-5'
    : reload.ok
      ? 'ki-check-circle'
      : 'ki-cross-circle';
  return (
    <div className={`alert ${cls} d-flex align-items-start p-4 mb-0`}>
      <Icon icon={icon} size="fs-2 me-3" />
      <div className="fs-7">
        <div className="fw-bold">
          {!reload.triggered
            ? `${what} no se disparó`
            : reload.ok
              ? `${what} aplicado en OpenSIPS`
              : `${what} FALLÓ — la fila quedó en la BD pero OpenSIPS sigue con la config anterior`}
        </div>
        <div className="text-muted">{reload.message}</div>
      </div>
    </div>
  );
}
